import { useState } from "react";
import axios from "axios";
import { ShieldCheck, AlertTriangle, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { API } from "@/lib/api";

const usd = (c) => `$${((c || 0) / 100).toFixed(2)}`;

function TripRow({ t }) {
  return (
    <div className="text-[11px] bg-black/30 rounded p-2 border border-white/5" data-testid={`audit-trip-${t.id}`}>
      <div className="flex justify-between gap-2">
        <span className="text-white font-bold">${t.subtotal.toFixed(2)} <span className="text-zinc-500 font-normal uppercase">{t.status}</span></span>
        <span className={t.points_awarded ? "text-emerald-300" : t.points_expected ? "text-red-300" : "text-zinc-500"}>
          {t.points_awarded ? "pts earned" : t.points_expected ? `missing ${t.points_expected} pts` : "—"}
        </span>
      </div>
      <p className="text-zinc-500 truncate">
        {t.date} · {t.payment_method || "?"}
        {!t.linked_to_account && <span className="text-amber-300"> · not linked</span>}
        {t.credits_applied_cents > 0 && (
          <span className="text-amber-300"> · used {usd(t.credits_applied_cents)}{t.credits_refunded ? " (refunded)" : ""}</span>
        )}
      </p>
    </div>
  );
}

export default function CreditsAuditPanel({ userId, onUpdated }) {
  const [audit, setAudit] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await axios.get(`${API}/admin/customers/${userId}/credits-audit`);
      setAudit(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Audit failed");
    } finally {
      setBusy(false);
    }
  };

  const fix = async (convert_points) => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/customers/${userId}/credits-fix`, { convert_points });
      setAudit(data.audit);
      onUpdated?.(data.audit);
      toast.success(
        `Fixed: +${data.points_added} pts, ${usd(data.credits_refunded_cents)} refunded` +
        (data.points_converted ? `, ${data.points_converted} pts → ${usd(data.credits_from_points_cents)} credit` : "")
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fix failed");
    } finally {
      setBusy(false);
    }
  };

  if (!audit) {
    return (
      <Button onClick={load} disabled={busy} variant="outline" className="w-full border-emerald-500/40 text-emerald-300 bg-transparent hover:bg-emerald-500/10" data-testid="credits-audit-btn">
        <ShieldCheck className="w-4 h-4 mr-2" /> {busy ? "Checking…" : "Run credits audit"}
      </Button>
    );
  }

  const s = audit.summary;
  return (
    <div className="space-y-2 rounded-lg border border-emerald-500/30 p-3 bg-emerald-500/5" data-testid="credits-audit-panel">
      <p className="text-[10px] uppercase tracking-[0.2em] text-emerald-300 font-bold">Credits audit</p>
      <div className="grid grid-cols-2 gap-2 text-[11px] text-zinc-300">
        <span>Wallet: <b className="text-amber-300" data-testid="audit-wallet">{usd(audit.wallet_credits_cents)}</b></span>
        <span>Points: <b className="text-emerald-300" data-testid="audit-points">{audit.loyalty_points}</b> (= {usd(audit.points_convertible_cents)})</span>
        <span>Trips: {s.completed_trips} done / {s.total_trips}</span>
        <span>Earned on: {s.trips_with_points} trips</span>
        <span>Credits spent: {usd(s.credits_spent_cents)}</span>
        <span>Refunded: {usd(s.credits_refunded_cents)}</span>
      </div>
      {audit.issues.length === 0 ? (
        <p className="text-[11px] text-emerald-300 flex items-center gap-1" data-testid="audit-no-issues"><CheckCircle2 className="w-3 h-3" /> Everything checks out.</p>
      ) : (
        <ul className="space-y-1" data-testid="audit-issues">
          {audit.issues.map((i) => (
            <li key={i} className="text-[11px] text-amber-200 flex gap-1.5"><AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" />{i}</li>
          ))}
        </ul>
      )}
      <div className="grid grid-cols-2 gap-2">
        <Button size="sm" onClick={() => fix(false)} disabled={busy} className="bg-emerald-600 hover:bg-emerald-500 text-white text-xs" data-testid="credits-fix-btn">
          {busy ? "…" : "Fix missing credits"}
        </Button>
        <Button size="sm" onClick={() => fix(true)} disabled={busy || audit.points_convertible_cents === 0} className="bg-amber-500 hover:bg-amber-400 text-black text-xs" data-testid="credits-fix-convert-btn">
          {busy ? "…" : "Fix + convert points"}
        </Button>
      </div>
      <div className="space-y-1 max-h-60 overflow-y-auto pr-1" data-testid="audit-trips">
        {audit.trips.map((t) => <TripRow key={t.id} t={t} />)}
      </div>
    </div>
  );
}
